import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { CategorySvgComponent } from '@curbnturf/custom-svgs/src/lib/category-svg/category-svg.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { ActivityLocation, ActivityType, IActivityLocation, IActivityLocationSelected } from '@curbnturf/entities';
import {
  activityLocationBuilder,
  EnumSelectorPipe,
  formatActivityLocationFormValue,
  IActivityLocationForm,
  photosFormBuilder,
} from '@curbnturf/form-helpers';
import { capitalize } from '@curbnturf/helpers';
import { LocationOptionalComponent } from '@curbnturf/location-interface/src/lib/location-optional/location-optional.component';
import { AlertController, IonicModule, IonicSafeString, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ActivityPhotosComponent } from '../activity-photos/activity-photos.component';

@Component({
  standalone: true,
  selector: 'curbnturf-add-activity-form',
  templateUrl: './add-activity-form.component.html',
  styleUrls: ['./add-activity-form.component.scss'],
  imports: [
    forwardRef(() => ActivityPhotosComponent),
    CategorySvgComponent,
    EnumSelectorPipe,
    IonicModule,
    LocationOptionalComponent,
    MiscSvgComponent,
    NgClass,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    SiteSvgsComponent,
  ],
})
export class AddActivityFormComponent implements OnChanges, OnDestroy, OnInit {
  @Input() activity: IActivityLocation;
  @Input() admin: boolean = false;
  @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();

  activitiesClosed: boolean = true;

  selected: IActivityLocationSelected = {
    activity: false,
    dump: false,
    restArea: false,
    wifi: false,
    potableWater: false,
    propane: false,
  };

  activityTypes = ActivityType;

  activityObject?: IActivityLocation;
  activityForm: FormGroup<IActivityLocationForm>;

  subscriptions: Subscription[] = [];

  error: string;

  showDescription = false;

  constructor(
    private poiFacade: ActivityLocationFacade,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private modalController: ModalController,
    public alertController: AlertController,
  ) {}

  ngOnInit() {
    if (this.activity) {
      this.activityObject = this.activity;
      this.activityForm = activityLocationBuilder(this.fb, this.activityObject);
    } else {
      this.subscriptions.push(
        this.poiFacade.awaitedActivity$.subscribe((awaitedActivity?: ActivityLocation) => {
          if (awaitedActivity) {
            this.activityObject = { ...awaitedActivity };
            this.poiFacade.cancelAwait();
            this.activityForm = activityLocationBuilder(this.fb, this.activityObject);
            this.updateActivity(awaitedActivity);

            this.cdr.detectChanges();

            this.subscriptions.push(
              this.poiFacade.selected$.subscribe((activity?: ActivityLocation) => {
                if (activity) {
                  this.activityObject = activity;
                  this.activityForm = activityLocationBuilder(this.fb, this.activityObject);
                  this.updateActivity(activity);
                  this.cdr.detectChanges();
                }
              }),
            );
          } else {
            if (!this.activityObject) {
              this.subscriptions.push(
                this.poiFacade.selected$.subscribe((activity?: ActivityLocation) => {
                  if (activity) {
                    this.activityObject = activity;
                    this.activityForm = activityLocationBuilder(this.fb, this.activityObject);
                    this.updateActivity(activity);
                    this.cdr.detectChanges();
                  } else {
                    // Create a new activity
                    const activityLocation = new ActivityLocation({});

                    this.activityObject = activityLocation;
                    this.activityForm = activityLocationBuilder(this.fb, this.activityObject);
                    this.updateActivity(activityLocation);
                    this.cdr.detectChanges();
                  }
                }),
              );
            }
          }
        }),
      );
    }

    this.subscriptions.push(
      this.poiFacade.activityEntities$.subscribe((activities) => {
        if (this.activity?.id) {
          const stateActivity = activities[this.activity.id];
          if (this.activity && this.activityObject && this.activityForm?.controls && stateActivity?.photos) {
            this.activity.photos = stateActivity.photos;
            this.activityObject.photos = stateActivity.photos;
            this.activityForm.controls.photos = photosFormBuilder(this.fb, stateActivity?.photos);
          }
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activity?.currentValue) {
      this.unsubscribeAll();
      this.updateActivity(changes.activity.currentValue);
    }
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  unsubscribeAll() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  updateActivity(activity: ActivityLocation) {
    this.activityObject = activity;
    this.activityForm = activityLocationBuilder(this.fb, this.activityObject);

    if (activity?.hasActivity && activity.hasActivity()) {
      this.selected.activity = true;
      this.activitiesClosed = false;
    } else {
      this.selected.activity = false;
      this.activitiesClosed = true;
    }

    this.selected.dump = !!activity.categoryDump;

    this.selected.restArea = !!activity.categoryRestArea;

    this.selected.wifi = !!activity.categoryWifi;

    this.selected.potableWater = !!activity.categoryPotableWater;

    this.selected.propane = !!activity.categoryPropane;
  }

  activityOpened(): boolean {
    return !!(this.selected.activity && !this.activitiesClosed);
  }

  toggleActivities() {
    this.selected.activity = !this.selected.activity;
    this.activitiesClosed = !this.selected.activity;
  }

  toggleDump() {
    this.selected.dump = !this.selected.dump;

    if (this.selected.dump) {
      this.clearErrors();
    }

    this.activityForm?.get('categoryDump')?.setValue(this.selected.dump);
  }

  toggleRestArea() {
    this.selected.restArea = !this.selected.restArea;

    if (this.selected.restArea) {
      this.clearErrors();
    }

    this.activityForm?.get('categoryRestArea')?.setValue(this.selected.restArea);
  }

  toggleWifi() {
    this.selected.wifi = !this.selected.wifi;

    if (this.selected.wifi) {
      this.clearErrors();
    }

    this.activityForm?.get('categoryWifi')?.setValue(this.selected.wifi);
  }

  togglePotableWater() {
    this.selected.potableWater = !this.selected.potableWater;

    if (this.selected.potableWater) {
      this.clearErrors();
    }

    this.activityForm?.get('categoryPotableWater')?.setValue(this.selected.potableWater);
  }

  togglePropane() {
    this.selected.propane = !this.selected.propane;

    if (this.selected.propane) {
      this.clearErrors();
    }

    this.activityForm?.get('categoryPropane')?.setValue(this.selected.propane);
  }

  activityControl(activity: string): FormControl<boolean | null> {
    return (
      (this.activityForm.get(`activity${capitalize(activity)}`) as unknown as FormControl<boolean | null>) ||
      this.fb.control<boolean | null>(null)
    );
  }

  activityChanged(activity: string): void {
    if (this.activityControl(activity)?.value) {
      this.clearErrors();
    }
  }

  async save() {
    if (this.activityForm && this.activityForm.valid) {
      const values = this.activityForm.value;
      if (!values.available && values.hidden) {
        const alert = await this.alertController.create({
          header: 'Enable Point of Interest',
          message: new IonicSafeString(`<p>
                It looks like your point of interest is complete. Would you like to enable
                it for everyone to see?
              </p>
              <p class="fine-print">
                After enabling the point of interest it will be reviewed by a moderator
                within the next 3 business days.
              </p>
          `),
          buttons: [
            {
              text: 'No',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => this.closeSave(),
            },
            {
              text: 'Yes',
              handler: () => this.showSave(),
            },
          ],
        });

        alert.present();
      } else {
        this.completeSave();
      }
    } else {
      if (this.activityForm.errors?.requiredPoiCategory) {
        this.error = 'A point of interest category or activity must be selected.';
      }
    }
  }

  clearErrors() {
    this.error = '';
    this.activityForm?.updateValueAndValidity();
  }

  closeModal() {
    this.close.emit(false);
  }

  setShowValue(show: boolean) {
    const hiddenForm = this.activityForm.get('hidden');
    // show is the inverse of hidden so we have to invert
    hiddenForm?.setValue(!show);
  }

  showSave() {
    this.setShowValue(true);
    this.completeSave();
  }

  closeSave() {
    this.completeSave();
  }

  private async completeSave() {
    if (this.activityForm.value?.id) {
      this.poiFacade.update(formatActivityLocationFormValue(this.activityForm));
    } else {
      this.poiFacade.create(formatActivityLocationFormValue(this.activityForm));
    }

    this.error = '';

    this.close.emit(true);
    const top = await this.modalController.getTop();
    if (top) {
      this.modalController.dismiss();
    }
  }
}
